import {
  buildSbefRootGroupAggregatesByScenarioId,
  maxCo2SekPerGfaFromScenarioGroupRows,
} from '@shared/helpers/group-section-bar-scale.helpers';
import type { ScenarioGroupAggregateRow } from '@shared/helpers/group-section-bar-scale.helpers';
import { filterListElementsContributingToVersionTotals } from '@shared/helpers/element-list-group-contributors.helpers';
import { getElementCategoryId } from '@shared/helpers/element.helpers';
import { sumConversionFactors } from '@shared/helpers/conversion-factors.helpers';
import { getActiveRootElementIdsInProposal } from '@shared/helpers/proposal.helpers';
import { isElement } from '@shared/helpers/recursive-element.helpers';
import type { Baseline, OneOfListElements } from '@shared/models/project.model';
import type { IProposal } from '@shared/models/proposals.model';
import type { Results, ResultsRecord } from '@shared/models/unit.model';

export type ElementListGroupBarScaleMax = {
  sbefGroupBarScale: { maxCo2: number; maxCost: number };
  productCategoryGroupBarScale: { maxCo2: number; maxCost: number };
};

export type ComputeElementListGroupBarScaleMaxParams = {
  version: Baseline;
  proposalsResultRecords: Record<string, ResultsRecord>;
  proposals: readonly IProposal[];
  getSbefGroupKey: (element: OneOfListElements) => string;
  getResultsPerGFA: (results: Results) => Results;
  /** When set, only these proposal ids are included (baseline {@link Baseline.id} is always included). */
  limitProposalIds?: ReadonlySet<string>;
  baselineElementResultsRecord?: ResultsRecord;
};

/**
 * Bar-scale maxima from {@link Baseline.elements} root results only.
 * Avoids {@link createProductListCategoryGroups} and full-tree proposal walks.
 */
const buildProductCategoryRootRowsByScenarioId = (
  version: Baseline,
  proposalsResultRecords: Record<string, ResultsRecord>,
  proposals: readonly IProposal[],
  limitProposalIds?: ReadonlySet<string>,
): Record<string, readonly ScenarioGroupAggregateRow[]> => {
  const roots = version.elements.filter(isElement);
  const contributingRoots = filterListElementsContributingToVersionTotals(
    roots,
    version,
  );
  const out: Record<string, ScenarioGroupAggregateRow[]> = {};

  const sumByCategory = (
    record: ResultsRecord,
    activeRootIds?: ReadonlySet<string>,
  ): ScenarioGroupAggregateRow[] => {
    const byCategory = new Map<string, Results>();
    for (const root of contributingRoots) {
      if (!isElement(root)) continue;
      if (activeRootIds && !activeRootIds.has(root.id)) continue;
      const groupKey = String(getElementCategoryId(root));
      byCategory.set(
        groupKey,
        sumConversionFactors(
          byCategory.get(groupKey) ?? {},
          record[root.id] ?? {},
        ),
      );
    }
    return [...byCategory.entries()].map(([groupKey, resultsSum]) => ({
      groupKey,
      resultsSum,
    }));
  };

  const baselineRecord = proposalsResultRecords[version.id] ?? {};
  out[version.id] = sumByCategory(baselineRecord);

  for (const proposal of proposals) {
    if (limitProposalIds && !limitProposalIds.has(proposal.id)) continue;
    const record = proposalsResultRecords[proposal.id];
    if (!record) continue;
    const activeRootIds = getActiveRootElementIdsInProposal(
      version,
      proposal,
      false,
    );
    out[proposal.id] = sumByCategory(record, activeRootIds);
  }

  return out;
};

/**
 * CO₂ / cost bar-scale denominators for SBEF group rows and product category headers.
 * Uses root-level {@link ResultsRecord} entries only; limits proposal work to
 * {@link ComputeElementListGroupBarScaleMaxParams.limitProposalIds} when provided.
 */
export const computeElementListGroupBarScaleMax = ({
  version,
  proposalsResultRecords,
  proposals,
  getSbefGroupKey,
  getResultsPerGFA,
  limitProposalIds,
  baselineElementResultsRecord,
}: ComputeElementListGroupBarScaleMaxParams): ElementListGroupBarScaleMax => {
  const baselineRecord =
    baselineElementResultsRecord ?? proposalsResultRecords[version.id] ?? {};
  const proposalRecordsByProposalId = { ...proposalsResultRecords };
  delete proposalRecordsByProposalId[version.id];

  const proposalsForSbef = limitProposalIds
    ? proposals.filter((p) => limitProposalIds.has(p.id))
    : proposals;

  const sbefGroupsByScenarioId = buildSbefRootGroupAggregatesByScenarioId({
    version,
    baselineRecord,
    proposalRecordsByProposalId,
    proposals: proposalsForSbef,
    getGroupKey: getSbefGroupKey,
  });

  const productCategoryGroupsByScenarioId =
    buildProductCategoryRootRowsByScenarioId(
      version,
      proposalsResultRecords,
      proposals,
      limitProposalIds,
    );

  const sbefPeak = maxCo2SekPerGfaFromScenarioGroupRows(
    sbefGroupsByScenarioId,
    getResultsPerGFA,
  );
  const categoryPeak = maxCo2SekPerGfaFromScenarioGroupRows(
    productCategoryGroupsByScenarioId,
    getResultsPerGFA,
  );

  return {
    sbefGroupBarScale: { maxCo2: sbefPeak.co2eMax, maxCost: sbefPeak.sekMax },
    productCategoryGroupBarScale: {
      maxCo2: categoryPeak.co2eMax,
      maxCost: categoryPeak.sekMax,
    },
  };
};
